import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HeaderComponent } from './header/header.component';
import { FooterComponent } from './footer/footer.component';
import { BrowserAnimationsModule, NoopAnimationsModule } from '@angular/platform-browser/animations';
import { HomeComponent } from './home/home.component';
import { PayrollComponent } from './payroll/payroll.component';
import { PayScheduleComponent } from './pay-schedule/pay-schedule.component';
import { ProfilesComponent } from './profiles/profiles.component';
import { ManageUsersComponent } from './profiles/manage-users/manage-users.component';
import { ManageBankingInfoComponent } from './profiles/manage-banking-info/manage-banking-info.component';
import { UploadInterceptor, UploadsComponent } from './uploads/uploads.component';
import { DocumentsComponent } from './documents/documents.component';
import { EmployerDocumentHistoryComponent } from './documents/employer-document-history/employer-document-history.component';
import { MessagesComponent } from './messages/messages.component';
import { HelpComponent } from './help/help.component';
import { ExcelModule, GridModule } from '@progress/kendo-angular-grid';
import { NewsComponent } from './news/news.component';
import { NewsDetailsComponent } from './news/news-details/news-details.component';
import { HttpClientJsonpModule, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NotificationModule } from '@progress/kendo-angular-notification';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { AddEditUsersComponent } from './profiles/manage-users/add-edit-users/add-edit-users.component';
import { AppConfigService } from './app-config.service';
import { ViewOrgInfoComponent } from './profiles/view-org-info/view-org-info.component';
import { NotificationLoadingComponent } from './shared/notification-loading/notification-loading.component';
import { PhonePipe } from './shared/pipes/phone.pipe';
import { CallbackComponent } from './callback/callback.component';
import { OAuthModule } from 'angular-oauth2-oidc';
import { LoginComponent } from './login/login.component';
import { AccountComponent } from './account/account.component';
import { OrgSelectComponent } from './app/org-select.component';
import { PopupModule } from '@progress/kendo-angular-popup';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DateInputsModule, DatePickerModule } from '@progress/kendo-angular-dateinputs';
import { TooltipModule } from '@progress/kendo-angular-tooltip';
import { ChangePasswordComponent } from './account/change-password/changepassword.component';
import { FilterIconDirective } from './shared/directives/filter-icon.directive';
import { UploadModule, UploadService } from '@progress/kendo-angular-upload';
import { LandingPageComponent } from './landing-page/landing-page.component';
import { SSNPipe } from './shared/pipes/ssn.pipe';
import { MemberDocUploadComponent } from './documents/member-doc-upload/member-doc-upload.component';
import { ScheduleDetailsComponent } from './pay-schedule/schedule-details/schedule-details.component';
import { CreateScheduleComponent } from './pay-schedule/create-schedule/create-schedule.component';
import { ContactInfoComponent } from './account/contact-info/contact-info.component';
import { DeleteScheduleComponent } from './pay-schedule/delete-schedule/delete-schedule.component';
import { AccountEndpoint } from './shared/services/account/account-endpoint.service';
import { AccountService } from './shared/services/account/account.service';
import { AuthGuard } from './shared/services/auth/auth-guard.service';
import { AuthInterceptor } from './shared/services/auth/auth-interceptor.service';
import { AuthService } from './shared/services/auth/auth.service';
import { ConfigurationService } from './shared/services/auth/configuration.service';
import { DBkeys } from './shared/services/auth/db-keys';
import { EndpointFactory } from './shared/services/auth/endpoint-factory.service';
import { LocalStoreManager } from './shared/services/auth/local-store-manager.service';
import { CommonService } from './shared/services/common/common.service';
import { DisplayNotificationService } from './shared/services/common/notification.service';
import { ReadConfigService } from './shared/services/common/read-config.service';
import { UserService } from './shared/services/common/user.service';
import { UsersService } from './shared/services/common/users.service';
import { ScheduleService } from './shared/services/pay-schedules/schedule.service';
import { ImportService } from './shared/services/payroll/import.service';
import { ProgressBarModule } from '@progress/kendo-angular-progressbar';
import { InvoiceDetailsComponent } from './invoice/invoice-details/invoice-details.component';
import { InvoiceComponent } from './invoice/invoice.component';
import { AdjustmentService } from './shared/services/adjustments/adjustment.service';
import { PayrollReportsComponent } from './payroll/payroll-reports/payroll-reports.component';
import { PayrollSummaryComponent } from './payroll/payroll-reports/payroll-summary/payroll-summary.component';
import { PayrollRecordComponent } from './payroll/payroll-reports/payroll-record/payroll-record.component';
import {
  PayrollRecordAddEditFormComponent
} from './payroll/payroll-reports/payroll-record-add-edit-form/payroll-record-add-edit-form.component';
import { AdjustmentReportsComponent } from './payroll/adjustment-reports/adjustment-reports.component';
import { AdjustmentSummaryComponent } from './payroll/adjustment-reports/adjustment-summary/adjustment-summary.component';
import { AdjustmentRecordComponent } from './payroll/adjustment-reports/adjustment-record/adjustment-record.component';
import { DateRangeFilterComponent } from './shared/date-range-filter/date-range-filter.component';
import { MessagesService } from './shared/services/messages/messages.service';
import { ViewMessageComponent } from './messages/view-message/view-message.component';
import { ComposeMessageComponent } from './messages/compose-message/compose-message.component';
import { MessageCountComponent } from './shared/message-count/message-count.component';
import { NoticeComponent } from './shared/notification/notification.component';
import { ForbiddenComponent } from './forbidden/forbidden.component';
import { ServiceErrorComponent } from './shared/service-error/service-error.component';
import { ManageUsersAndContactComponent } from './profiles/manage-users-and-contact/manage-users-and-contact.component';
import {
  AddEditUsersAndContactComponent
} from './profiles/manage-users-and-contact/add-edit-users-and-contact/add-edit-users-and-contact.component';
import { OutageNoticeComponent } from './outage-notice/outage-notice.component';
import { MemberDocumentHistoryComponent } from './documents/member-document-history/member-document-history.component';
import { EmployeesComponent } from './employees/employees.component';
import { TerminateComponent } from './employees/terminate/terminate.component';
import { EmployeesService } from './shared/services/employees/employees.service';
import { EarningsLimitationComponent } from './uploads/earnings-limitation/earnings-limitation.component';
import { WageFileComponent } from './uploads/earnings-limitation/wage-file/wage-file.component';
import { ExemptionsComponent } from './uploads/earnings-limitation/exemptions/exemptions.component';
import { MemberDocumentsComponent } from './uploads/member-documents/member-documents.component';
import { SlRecertificationComponent } from './uploads/sl-recertification/sl-recertification.component';
import { RetireeDeductionsComponent } from './uploads/retiree-deductions/retiree-deductions.component';
import { UploadHistoryComponent } from './shared/upload-history/upload-history.component';
import { AdjustmentReportsV2Component } from './payroll/adjustment-reports-v2/adjustment-reports-v2.component';
import { CreateAdjustmentReportComponent } from './payroll/adjustment-reports-v2/create-adjustment-report/create-adjustment-report.component';
import { ViewAdjustmentReportsComponent } from './payroll/adjustment-reports-v2/view-adjustment-reports/view-adjustment-reports.component';
import { RejectedAdjustmentRecordsComponent } from './payroll/adjustment-reports-v2/rejected-adjustment-records/rejected-adjustment-records.component';
import { AdjRecordAddEditFormComponent } from './payroll/adjustment-reports-v2/create-adjustment-report/adj-record-add-edit-form/adj-record-add-edit-form.component';
import { RequiredValidator } from './shared/directives/required.directive';
import { DetailedViewComponent } from './payroll/adjustment-reports-v2/view-adjustment-reports/detailed-view/detailed-view.component';
import { RecordDetailViewComponent } from './payroll/adjustment-reports-v2/record-detail-view/record-detail-view.component';
import { InvalidGridComponent } from './payroll/adjustment-reports-v2/create-adjustment-report/invalid-grid/invalid-grid.component';
import { ValidGridComponent } from './payroll/adjustment-reports-v2/create-adjustment-report/valid-grid/valid-grid.component';
import { ValidDetailViewComponent } from './payroll/adjustment-reports-v2/create-adjustment-report/valid-grid/valid-detail-view/valid-detail-view.component';
import { InvalidDetailViewComponent } from './payroll/adjustment-reports-v2/create-adjustment-report/invalid-grid/invalid-detail-view/invalid-detail-view.component';
import { ChildDetailedViewComponent } from './payroll/adjustment-reports-v2/view-adjustment-reports/detailed-view/child-detailed-view/child-detailed-view.component';
import { ErrorGridComponent } from './payroll/adjustment-reports-v2/create-adjustment-report/error-grid/error-grid.component';
import { ErrorDetailViewComponent } from './payroll/adjustment-reports-v2/create-adjustment-report/error-grid/error-detail-view/error-detail-view.component';
import { TaskTrackerComponent } from './task-tracker/task-tracker.component';
import { ActiveTaskComponent } from './task-tracker/active-task/active-task.component';
import { CompletedTaskComponent } from './task-tracker/completed-task/completed-task.component';
import { CorrespondingViewComponent } from './payroll/adjustment-reports-v2/create-adjustment-report/corresponding-view/corresponding-view.component';
import { OtherComponent } from './uploads/other/other.component';
import { CutoffNoticesComponent } from './employees/cutoff-notices/cutoff-notices.component';
import { EnrollmentsComponent } from './employees/enrollments/enrollments.component';
import { EnrollMemberComponent } from './employees/enrollments/enroll-member/enroll-member.component';
import { PersonInfoComponent } from './employees/enrollments/enroll-member/person-info/person-info.component';
import { EnrollmentInfoComponent } from './employees/enrollments/enroll-member/enrollment-info/enrollment-info.component';
import { EnrollmentContactInfoComponent } from './employees/enrollments/enroll-member/contact-info/contact-info.component';
import { PendingEnrollmentsComponent } from './employees/enrollments/pending-enrollments/pending-enrollments.component';
import { ProcessedEnrollmentsComponent } from './employees/enrollments/processed-enrollments/processed-enrollments.component';
import { PendingDetailedViewComponent } from './employees/enrollments/pending-enrollments/pending-detailed-view/pending-detailed-view.component';


const appInitializerFn = (appConfig: AppConfigService) => {
  return () => {
    return appConfig.loadAppConfig();
  };
};

@NgModule({
    declarations: [
        AppComponent,
        HeaderComponent,
        FooterComponent,
        HomeComponent,
        PayrollComponent,
        InvoiceComponent,
        PayScheduleComponent,
        ProfilesComponent,
        ContactInfoComponent,
        ManageUsersComponent,
        ManageBankingInfoComponent,
        UploadsComponent,
        DocumentsComponent,
        EmployerDocumentHistoryComponent,
        MessagesComponent,
        HelpComponent,
        NewsComponent,
        NewsDetailsComponent,
        AddEditUsersComponent,
        PhonePipe,
        ViewOrgInfoComponent,
        NotificationLoadingComponent,
        CallbackComponent,
        LoginComponent,
        AccountComponent,
        OrgSelectComponent,
        ChangePasswordComponent,
        FilterIconDirective,
        RequiredValidator,
        LandingPageComponent,
        MemberDocUploadComponent,
        SSNPipe,
        ScheduleDetailsComponent,
        CreateScheduleComponent,
        DeleteScheduleComponent,
        PayrollReportsComponent,
        PayrollSummaryComponent,
        PayrollRecordComponent,
        PayrollRecordAddEditFormComponent,
        InvoiceDetailsComponent,
        AdjustmentSummaryComponent,
        AdjustmentRecordComponent,
        AdjustmentReportsComponent,
        DateRangeFilterComponent,
        ViewMessageComponent,
        ComposeMessageComponent,
        MessageCountComponent,
        NoticeComponent,
        ForbiddenComponent,
        ServiceErrorComponent,
        ManageUsersAndContactComponent,
        AddEditUsersAndContactComponent,
        OutageNoticeComponent,
        MemberDocumentHistoryComponent,
        EmployeesComponent,
        TerminateComponent,
        EarningsLimitationComponent,
        WageFileComponent,
        ExemptionsComponent,
        MemberDocumentsComponent,
        SlRecertificationComponent,
        RetireeDeductionsComponent,
        UploadHistoryComponent,
        AdjustmentReportsV2Component,
        CreateAdjustmentReportComponent,
        ViewAdjustmentReportsComponent,
        RejectedAdjustmentRecordsComponent,
        AdjRecordAddEditFormComponent,
        DetailedViewComponent,
        RecordDetailViewComponent,
        InvalidGridComponent,
        ValidGridComponent,
        ValidDetailViewComponent,
        InvalidDetailViewComponent,
        ChildDetailedViewComponent,
        ErrorGridComponent,
        ErrorDetailViewComponent,
        TaskTrackerComponent,
        ActiveTaskComponent,
        CompletedTaskComponent,
        CorrespondingViewComponent,
        OtherComponent,
        CutoffNoticesComponent,
        EnrollmentsComponent,
        EnrollMemberComponent,
        PersonInfoComponent,
        EnrollmentInfoComponent,
        EnrollmentContactInfoComponent,
        PendingEnrollmentsComponent,
        ProcessedEnrollmentsComponent,
        PendingDetailedViewComponent
    ],
    imports: [
        BrowserModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        NoopAnimationsModule,
        HttpClientJsonpModule,
        FormsModule,
        CommonModule,
        ButtonsModule,
        ReactiveFormsModule,
        DropDownsModule,
        InputsModule,
        GridModule,
        ExcelModule,
        DialogsModule,
        PopupModule,
        LayoutModule,
        DateInputsModule,
        DatePickerModule,
        OAuthModule.forRoot(),
        NotificationModule,
        HttpClientModule,
        UploadModule,
        TooltipModule,
        ProgressBarModule
    ],
    providers: [
        AppConfigService,
        CommonService,
        ReadConfigService,
        ConfigurationService,
        AuthService,
        UsersService,
        UploadService,
        AccountService,
        UserService,
        EndpointFactory,
        DBkeys,
        AuthGuard,
        LocalStoreManager,
        DisplayNotificationService,
        AccountEndpoint,
        ImportService,
        AdjustmentService,
        ScheduleService,
        EmployeesService,
        MessagesService,
        { provide: 'Window', useValue: window },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: UploadInterceptor,
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: appInitializerFn,
            multi: true,
            deps: [AppConfigService]
        }
    ],
    bootstrap: [AppComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule { }
